<template>
  <div class="form-group">
    <label v-if="labelName" :for="inputId + '-day'" class="form-label">
      {{ labelName }} <span class="format-hint">(DD/MM/YYYY)</span>
    </label>

    <div
      class="simplified-date-input"
      :class="{ 'is-invalid': errorMessage, disabled: disabled }"
    >
      <input
        :id="inputId + '-day'"
        ref="dayRef"
        :value="dayValue"
        type="text"
        inputmode="numeric"
        class="date-segment"
        maxlength="2"
        placeholder="DD"
        :disabled="disabled"
        @input="handleDayInput"
        @keydown="handleKeydown($event, 'day')"
        @blur="handleBlur"
      />
      <div class="date-divider"></div>
      <input
        :id="inputId + '-month'"
        ref="monthRef"
        :value="monthValue"
        type="text"
        inputmode="numeric"
        class="date-segment"
        maxlength="2"
        placeholder="MM"
        :disabled="disabled"
        @input="handleMonthInput"
        @keydown="handleKeydown($event, 'month')"
        @blur="handleBlur"
      />
      <div class="date-divider"></div>
      <input
        :id="inputId + '-year'"
        ref="yearRef"
        :value="yearValue"
        type="text"
        inputmode="numeric"
        class="date-segment date-segment--year"
        maxlength="4"
        placeholder="YYYY"
        :disabled="disabled"
        @input="handleYearInput"
        @keydown="handleKeydown($event, 'year')"
        @blur="handleBlur"
      />
    </div>

    <div v-if="errorMessage" class="invalid-feedback d-block">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";

const props = defineProps({
  labelName: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  inputId: {
    type: String,
    default: "simplified-date-input",
  },
  modelValue: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: true,
  },
  maxDate: {
    type: Date,
    default: null,
  },
  minDate: {
    type: Date,
    default: null,
  },
  externalError: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "valid", "blur"]);

const { t } = useI18n();
const dayRef = ref<HTMLInputElement | null>(null);
const monthRef = ref<HTMLInputElement | null>(null);
const yearRef = ref<HTMLInputElement | null>(null);
const dayValue = ref("");
const monthValue = ref("");
const yearValue = ref("");
const internalError = ref("");

const errorMessage = computed(() => props.externalError || internalError.value);

const rawDigits = computed(() => {
  return dayValue.value + monthValue.value + yearValue.value;
});

const parseToDate = (digits: string): Date | null => {
  if (digits.length !== 8) return null;

  const day = parseInt(digits.slice(0, 2), 10);
  const month = parseInt(digits.slice(2, 4), 10) - 1;
  const year = parseInt(digits.slice(4, 8), 10);

  if (month < 0 || month > 11) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;

  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const formatToIso = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const emitIfComplete = () => {
  const digits = rawDigits.value;
  if (digits.length === 8) {
    const date = parseToDate(digits);
    if (date) {
      emit("update:modelValue", formatToIso(date));
      validate();
    }
  }
};

const processModelValue = (value: string | null | undefined) => {
  if (!value) {
    dayValue.value = "";
    monthValue.value = "";
    yearValue.value = "";
    return;
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    dayValue.value = day;
    monthValue.value = month;
    yearValue.value = year;
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    processModelValue(newValue);
  },
  { immediate: true }
);

const handleDayInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const digits = input.value.replace(/\D/g, "").slice(0, 2);
  dayValue.value = digits;
  internalError.value = "";
  if (digits.length === 2) {
    nextTick(() => monthRef.value?.focus());
  }
  emitIfComplete();
};

const handleMonthInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const digits = input.value.replace(/\D/g, "").slice(0, 2);
  monthValue.value = digits;
  internalError.value = "";
  if (digits.length === 2) {
    nextTick(() => yearRef.value?.focus());
  }
  emitIfComplete();
};

const handleYearInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const digits = input.value.replace(/\D/g, "").slice(0, 4);
  yearValue.value = digits;
  internalError.value = "";
  emitIfComplete();
};

const handleKeydown = (event: KeyboardEvent, segment: "day" | "month" | "year") => {
  if (
    event.key === "Tab" ||
    event.key === "Escape" ||
    event.key === "Enter" ||
    event.key === "ArrowLeft" ||
    event.key === "ArrowRight"
  ) {
    return;
  }

  if (event.key === "Backspace") {
    const input = event.target as HTMLInputElement;
    if (input.value.length === 0) {
      event.preventDefault();
      if (segment === "year") {
        monthRef.value?.focus();
      } else if (segment === "month") {
        dayRef.value?.focus();
      }
    }
    return;
  }

  if (event.key === "Delete") {
    return;
  }

  if (!/^\d$/.test(event.key)) {
    event.preventDefault();
  }
};

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement | null;
  const container = (event.target as HTMLElement)?.closest(".simplified-date-input");
  if (container && relatedTarget && container.contains(relatedTarget)) {
    return;
  }
  validate();
  emit("blur", event);
};

const validate = (): boolean => {
  internalError.value = "";
  const digits = rawDigits.value;

  if (!digits && props.required) {
    internalError.value = t("DATE_ERROR_TEXT");
    emit("valid", false);
    return false;
  }

  if (digits && digits.length !== 8) {
    internalError.value = t("DATE_ERROR_TEXT_INCOMPLETE");
    emit("valid", false);
    return false;
  }

  const date = parseToDate(digits);
  if (digits && !date) {
    internalError.value = t("DATE_ERROR_TEXT_INVALID");
    emit("valid", false);
    return false;
  }

  if (date && props.maxDate && date > props.maxDate) {
    internalError.value = t("DATE_ERROR_TEXT_TOO_RECENT");
    emit("valid", false);
    return false;
  }

  if (date && props.minDate && date < props.minDate) {
    internalError.value = t("DATE_ERROR_TEXT_TOO_OLD");
    emit("valid", false);
    return false;
  }

  emit("valid", true);

  if (date) {
    emit("update:modelValue", formatToIso(date));
  }

  return true;
};

const getInputFieldValue = () => {
  const date = parseToDate(rawDigits.value);
  return date ? formatToIso(date) : "";
};

const focus = () => {
  dayRef.value?.focus();
};

const getDate = (): Date | null => {
  return parseToDate(rawDigits.value);
};

defineExpose({
  validate,
  getInputFieldValue,
  getDate,
  focus,
});
</script>

<style lang="scss" scoped>
.simplified-date-input {
  display: flex;
  align-items: center;
  border: 2px solid #bdb9c6;
  border-radius: 11px;
  height: 48px;
  overflow: hidden;
  background-color: #fff;

  &.is-invalid {
    border-color: #dc3545;
  }

  &.disabled {
    background-color: #e9ecef;
  }
}

.date-segment {
  flex: 1 1 0;
  min-width: 0;
  border: none;
  outline: none;
  height: 100%;
  font-size: 1rem;
  text-align: center;
  padding: 0;

  &--year {
    flex: 1.5 1 0;
  }

  &:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #adb5bd;
  }
}

.date-divider {
  width: 1px;
  height: 60%;
  background-color: #bdb9c6;
  flex-shrink: 0;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.format-hint {
  color: #6c757d;
  font-weight: 400;
}
</style>
